import React, { ReactNode, FC, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import { OutsideClickHandler } from 'src/presentation/components/agent_desktop_components';
import { ButtonTypes, PortalModalWrapper, Button } from 'src/presentation/components/agent_desktop_components';
import { H2 } from '@ad-scripts/presentation/components/common/headers';

type ActionsModalProps = {
  cancelText?: string;
  children: ReactNode[];
  className?: string;
  disableSubmitButton?: boolean;
  onCancel: Function;
  onSubmit: Function;
  submitButtonType?: string;
  submitText?: string;
  text?: string;
  title?: string;
  'data-aid'?: string;
};

const ActionsModal: FC<ActionsModalProps> = ({
  cancelText = 'Cancel',
  children,
  className,
  disableSubmitButton = false,
  onCancel,
  onSubmit,
  submitButtonType = ButtonTypes.PRIMARY,
  submitText = 'Confirm',
  text,
  title,
  'data-aid': dataAid = 'actionsModal',
}) => {
  // Prevent scrolling body underneath open modal
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const onClickSend = useCallback(evt => onSubmit(evt, true), [onSubmit]);

  const actions = (
    <Actions data-aid={`${dataAid}-actions`}>
      <CancelButton data-aid={`${dataAid}-cancel`} buttonType={ButtonTypes.TEXT} onClick={onCancel}>
        {cancelText}
      </CancelButton>
      <SubmitButton
        data-aid={`${dataAid}-submit`}
        buttonType={submitButtonType}
        disabled={disableSubmitButton}
        onClick={onClickSend}
      >
        {submitText}
      </SubmitButton>
    </Actions>
  );

  return (
    <PortalModalWrapper className={className}>
      <ModalContainer data-aid={`${dataAid}`}>
        <OutsideClickHandler onClickOutside={onCancel}>
          <ModalContent data-aid={`${dataAid}-content`}>
            <Header>{title}</Header>
            {text && <p>{text}</p>}
            {children}
            {actions}
          </ModalContent>
        </OutsideClickHandler>
      </ModalContainer>
    </PortalModalWrapper>
  );
};

export const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.white};
  border-radius: 8px;
  box-shadow: ${p => p.theme.boxShadow.large};
  height: 540px;
  line-height: 162%;
  padding: 0 0 50px;
  position: relative;
  text-align: left;
  width: 770px;
`;

export const ModalContainer = styled.div`
  align-items: center;
  backdrop-filter: ${p => p.theme.overlay.backdropFilter};
  background-color: ${p => p.theme.overlay.color};
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10;
`;

export const Header = styled(H2)`
  display: inline;
`;

export const Actions = styled.div`
  bottom: 0;
  display: flex;
  height: 46px;
  justify-content: center;
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.large};
  padding: 0 12px 12px;
  position: absolute;
  right: 0;
`;

const CancelButton = styled(Button)`
  color: ${p => p.theme.colors.gray600};
`;

const SubmitButton = styled(Button)`
  margin-left: ${p => p.theme.spacing.large};
`;

export default ActionsModal;
